import * as React from "react";
import Note from "../../../model/note/Note";
import {JobEventsType} from "../../../model/job/event/JobEvent";
import JobEventTypeObject, {JobEventCategory} from "../../../model/job/event/JobEventTypeObject";
import LocalStorage from "../../../store/local/LocalStorage";
import MultiSelectDropDown from "../../../hiberUiKit/src/molecules/MultiSelectDropDown/MultiSelectDropDown";
import JobEventCategoryType from "../../../model/job/event/JobEventCategoryType";
import ActivityStreamUtils from "../../../utils/ActivityStreamUtils";
import JobActivityStreamItems from "./JobActivityStreamItems";
import {LeadEventType} from "../../../model/lead/event/LeadEvent";
import {LeadActivityItemsType} from "../../activityStream/LeadActivityStream";
import {default as LeadEventTypeObject, LeadEventCategory} from "../../../model/lead/event/LeadEventTypeObject";
import LeadEventCategoryType from "../../../model/lead/event/LeadEventCategoryType";
import {Dispatch} from "redux";
import Job from "../../../model/job/Job";
import {editJob} from "../../../store/action/job/job";
import {connect} from "react-redux";
import {OnSuccess} from "../../../store/action/actionHelpers";
import {option, Option} from "ts-option";
import {notifyBugsnagAndShowToast} from "../../../utils/Utils";
import ShowBillInMobileSwitch from "../../accounting/common/ShowBillInMobileSwitch";
import Bill from "../../../model/bill/Bill";
import InvoiceEvent, {InvoiceEventType} from "../../../model/invoice/event/InvoiceEvent";
import AccountingNote from "../../../model/invoice/note/AccountingNote";
import {InvoiceActivityItemsType} from "../../accounting/activityStream/InvoiceActivityStream";
import InvoiceEventTypeObject, {InvoiceEventCategory} from "../../../model/invoice/event/InvoiceEventTypeObject";
import Payment from "../../../model/payment/Payment";
import InvoiceEventCategoryType from "../../../model/invoice/event/InvoiceEventCategoryType";
import * as _ from "underscore";
import {RootState} from "../../../store/reducer/root/RootState";
import deepEqual = require("deep-equal");

export type JobActivityItemsType = JobEventsType | Note;

interface ComponentProps {
	/**
	 * Job notes.
	 */
	notes: Note[]
	/**
	 * Lead notes.
	 */
	leadNotes: Note[]
	/**
	 * Invoice notes.
	 */
	invoiceNotes: AccountingNote[];
	/**
	 * Job events.
	 */
	jobEvents: Array<JobEventsType>
	/**
	 * Lead related to this job events.
	 */
	leadEvents: Array<LeadEventType>
	/**
	 * Invoice events.
	 */
	invoiceEvents: InvoiceEvent[];
	/**
	 * Invoice payments
	 */
	invoicePayments: Payment[];
	/**
	 * Note id to set active class and focus on it.
	 */
	activeNoteId: string;
	/**
	 * Job to be marked as parts only / reverted to be normal job.
	 */
	job: Option<Job>;
	/**
	 * Bill related to job.
	 */
	bill: Option<Bill>;
	/**
	 * Edit note click action.
	 * @param note note to edit.
	 */
	onEditNoteClick: (note: Note) => void;
	/**
	 * Edit appointment click action.
	 * @param appointment to edit.
	 */
	onEditAppointmentClick: (appointmentId: string) => void;
	/**
	 * Parts only flag change callback.
	 */
	onPartsOnlyStatusChange: (partsOnly: boolean) => void;
}

interface DispatchProps {
	/**
	 * Edit job action.
	 * @param {Job} job
	 */
	editJob: (job: Job, onSuccess: OnSuccess) => void;
}

interface State {
	/**
	 * Job activity data.
	 */
	activityData: JobActivityItemsType[];
	/**
	 * Lead activity data.
	 */
	leadActivityData: LeadActivityItemsType[];
	/**
	 * Invoice activity data.
	 */
	invoiceActivityData: InvoiceActivityItemsType[];
	/**
	 * Filtered activity data
	 */
	filteredActivityData: JobActivityItemsType[];
	/**
	 * Filtered lead activity data
	 */
	filteredLeadActivityData: LeadActivityItemsType[];
	/**
	 * Filtered invoice activity data.
	 */
	filteredInvoiceActivityData: InvoiceActivityItemsType[];
	/**
	 * Selected filters
	 */
	selectedFiltersIds: string[];
	/**
	 * Selected lead filters
	 */
	selectedLeadFiltersIds: string[];
	/**
	 * Selected invoice filters
	 */
	selectedInvoiceFiltersIds: string[];
}

interface ReduxProps {
	/**
	 * Is Labour only job flag
	 */
	isLabourOnlyJob: boolean
}

type Props = ComponentProps & DispatchProps & ReduxProps;

/**
 * Job activity stream component.
 */
class JobActivityStream extends React.PureComponent<Props, State> {
	private filterList: JobEventCategoryType[] = JobEventCategoryType.getAllCategories();
	private leadFilterList: LeadEventCategoryType[] = LeadEventCategoryType.getAllCategories();
	private invoiceFilterList: InvoiceEventCategoryType[] = InvoiceEventCategoryType.getAllCategories();

	constructor(props: Props) {
		super(props);
		const selectedFiltersIds = LocalStorage.getJobActivityFilterIds();
		const activityData = this.mergeActivityStreamItems(props.jobEvents, props.notes);
		const filteredActivityData = this.filterActivityData(activityData, selectedFiltersIds);
		const selectedLeadFiltersIds = LocalStorage.getJobStreamLeadActivityFilterIds();
		const selectedInvoiceFiltersIds = LocalStorage.getJobStreamInvoiceActivityFilterIds();
		const leadActivityData = this.mergeActivityStreamItems(props.leadEvents, props.leadNotes);
		const invoiceActivityData = this.mergeActivityStreamItems(props.invoiceEvents, props.invoiceNotes, props.invoicePayments);
		const filteredLeadActivityData = this.filterLeadActivityData(leadActivityData, selectedLeadFiltersIds);
		const filteredInvoiceActivityData = this.filterInvoiceActivityData(invoiceActivityData, selectedInvoiceFiltersIds);
		this.state = {
			activityData,
			filteredActivityData,
			selectedFiltersIds,
			leadActivityData,
			filteredLeadActivityData,
			selectedLeadFiltersIds,
			invoiceActivityData,
			filteredInvoiceActivityData,
			selectedInvoiceFiltersIds
		};
		this.updateFilter = this.updateFilter.bind(this);
		this.updateLeadActivityFilter = this.updateLeadActivityFilter.bind(this);
		this.updateInvoiceActivityFilter = this.updateInvoiceActivityFilter.bind(this);
		this.renderTopControlsPanel = this.renderTopControlsPanel.bind(this);
	}

	componentWillReceiveProps(newProps: Props) {
		if (!deepEqual(newProps.notes, this.props.notes) || !deepEqual(newProps.jobEvents, this.props.jobEvents)) {
			const activityData = this.mergeActivityStreamItems(newProps.jobEvents, newProps.notes);
			const filteredActivityData = this.filterActivityData(activityData, this.state.selectedFiltersIds);
			this.setState({activityData, filteredActivityData});
		}
		if (!deepEqual(newProps.leadNotes, this.props.leadNotes) || !deepEqual(newProps.leadEvents, this.props.leadEvents)) {
			const leadActivityData = this.mergeActivityStreamItems(newProps.leadEvents, newProps.leadNotes);
			const filteredLeadActivityData = this.filterLeadActivityData(leadActivityData, this.state.selectedLeadFiltersIds);
			this.setState({leadActivityData, filteredLeadActivityData});
		}
		if (!deepEqual(newProps.invoiceNotes, this.props.invoiceNotes) ||
			!deepEqual(newProps.invoiceEvents, this.props.invoiceEvents) ||
			!deepEqual(newProps.invoicePayments, this.props.invoicePayments)) {
			const invoiceActivityData = this.mergeActivityStreamItems(newProps.invoiceEvents, newProps.invoiceNotes,
				newProps.invoicePayments);
			const filteredInvoiceActivityData = this.filterInvoiceActivityData(invoiceActivityData,
				this.state.selectedInvoiceFiltersIds);
			this.setState({invoiceActivityData, filteredInvoiceActivityData});
		}
	}

	componentWillUnmount() {
		this.filterList = null;
		this.leadFilterList = null;
		this.invoiceFilterList = null;
	}

	render() {
		return (
			<>
				{this.renderTopControlsPanel()}
				<div className="l-lead-details-filter-container c-job-stream-filter">
					<div className="c-job-stream-filter_single-filter-container">
						<div>Job filter</div>
						<MultiSelectDropDown items={this.filterList}
											 itemToId={(item: JobEventCategoryType) => item.id}
											 itemToLabel={(item: JobEventCategoryType) => item.getLabel()}
											 selectedItemIds={this.state.selectedFiltersIds}
											 onSelect={this.updateFilter}/>
					</div>
					<div className="c-job-stream-filter_single-filter-container">
						<div>Lead filter</div>
						<MultiSelectDropDown items={this.leadFilterList}
											 itemToId={(item: LeadEventCategoryType) => item.id}
											 itemToLabel={(item: LeadEventCategoryType) => item.getLabel()}
											 selectedItemIds={this.state.selectedLeadFiltersIds}
											 onSelect={this.updateLeadActivityFilter}/>
					</div>
					<div className="c-job-stream-filter_single-filter-container">
						<div>Invoice filter</div>
						<MultiSelectDropDown items={this.invoiceFilterList}
											 itemToId={(item: InvoiceEventCategoryType) => item.id}
											 itemToLabel={(item: InvoiceEventCategoryType) => item.getLabel()}
											 selectedItemIds={this.state.selectedInvoiceFiltersIds}
											 onSelect={this.updateInvoiceActivityFilter}/>
					</div>
				</div>
				<div className={this.state.filteredActivityData.length === 1 ? "is-single" : ""}>
					<JobActivityStreamItems activeNoteId={this.props.activeNoteId}
											activityItems={this.state.filteredActivityData}
											leadActivityItems={this.state.filteredLeadActivityData}
											invoiceActivityItems={this.state.filteredInvoiceActivityData}
											onEditNoteClick={this.props.onEditNoteClick}
											onEditAppointmentClick={this.props.onEditAppointmentClick}/>
				</div>
			</>
		)
	}

	renderTopControlsPanel() {
		if (!this.props.isLabourOnlyJob) {
			return (
				<div className="c-job-stream-filter_job-controls-container">
					{this.props.bill.map(b =>
						<div style={{display: "inline-flex"}}>
							<ShowBillInMobileSwitch bill={b}/>
						</div>).orNull}
				</div>
			)
		}
		return null;
	}

	/**
	 * Update filter and save filter in local storage
	 * @param {JobActivityItemsType[]} filters
	 */
	updateFilter(filters: JobEventTypeObject[]) {
		const selectedFiltersIds = filters.map(t => t.id);
		const filteredActivityData = this.filterActivityData(this.state.activityData, selectedFiltersIds);
		LocalStorage.saveJobActivityFilterIds(selectedFiltersIds);

		this.setState({selectedFiltersIds, filteredActivityData})
	}

	/**
	 * Update filter and save filter in local storage
	 * @param {LeadEventTypeObject[]} filters
	 */
	updateLeadActivityFilter(filters: LeadEventTypeObject[]) {
		const selectedLeadFiltersIds = filters.map(t => t.id);
		const filteredLeadActivityData = this.filterLeadActivityData(this.state.leadActivityData, selectedLeadFiltersIds);
		LocalStorage.saveJobStreamLeadActivityFilterIds(selectedLeadFiltersIds);

		this.setState({selectedLeadFiltersIds, filteredLeadActivityData});
	}

	/**
	 * Update filter and save filter in local storage
	 * @param {InvoiceEventTypeObject[]} filters
	 */
	updateInvoiceActivityFilter(filters: InvoiceEventTypeObject[]) {
		const selectedInvoiceFiltersIds = filters.map(t => t.id);
		const filteredInvoiceActivityData = this.filterInvoiceActivityData(this.state.invoiceActivityData,
			selectedInvoiceFiltersIds);
		LocalStorage.saveJobStreamInvoiceActivityFilterIds(selectedInvoiceFiltersIds);

		this.setState({selectedInvoiceFiltersIds, filteredInvoiceActivityData});
	}

	/**
	 * Filter activity data by selected filters
	 * @param {JobActivityItemsType[]} activityData
	 * @param {string[]} filters
	 * @returns {JobActivityItemsType[]}
	 */
	filterActivityData(activityData: JobActivityItemsType[], filters: string[]) {
		let filteredData: JobActivityItemsType[] = [];
		filters.forEach(filter => filteredData = filteredData.concat(this.filterSingleCategory(filter, activityData)));
		return filteredData.sort(ActivityStreamUtils.sort);
	}

	/**
	 * Filter lead activity data by selected filters
	 * @param {LeadActivityItemsType[]} activityData
	 * @param {string[]} filters
	 * @returns {LeadActivityItemsType[]}
	 */
	filterLeadActivityData(activityData: LeadActivityItemsType[], filters: string[]) {
		let filteredData: LeadActivityItemsType[] = [];
		filters.forEach(
			filter => filteredData = filteredData.concat(this.filterSingleLeadActivityCategory(filter, activityData)));
		return filteredData.sort(ActivityStreamUtils.sort);
	}

	/**
	 * Filter invoice activity data by selected filters
	 * @param {InvoiceActivityItemsType[]} activityData
	 * @param {string[]} filters
	 * @returns {InvoiceActivityItemsType[]}
	 */
	filterInvoiceActivityData(activityData: InvoiceActivityItemsType[], filters: string[]) {
		let filteredData: InvoiceActivityItemsType[] = [];
		filters.forEach(
			filter => filteredData = filteredData.concat(this.filterSingleInvoiceActivityCategory(filter, activityData)));
		return _.uniq(filteredData, item => item.id).sort(ActivityStreamUtils.sort);
	}

	/**
	 * Filter single category.
	 * @param {string} filter key of the category.
	 * @param {LeadActivityItemsType[]} activityData All data to filter.
	 * @return {LeadActivityItemsType[]} Filtered data by key.
	 */
	filterSingleCategory(filter: string, activityData: JobActivityItemsType[]) {
		if (filter === JobEventCategory.Notes) {
			return activityData.filter(ad => ad instanceof Note && ad.reminderDate.isEmpty);
		}
		if (filter === JobEventCategory.Reminders) {
			return activityData.filter(ad => ad instanceof Note && ad.reminderDate.isDefined);
		}
		else {
			return activityData.filter(ad =>
				!(ad instanceof Note) &&
				JobEventTypeObject.getTypesForCategory(filter).map((jto: JobEventTypeObject) => jto.id).indexOf(ad.type) > -1);
		}
	}

	/**
	 * Filter single lead activity category.
	 * @param {string} filter key of the category.
	 * @param {LeadActivityItemsType[]} activityData All data to filter.
	 * @return {LeadActivityItemsType[]} Filtered data by key.
	 */
	filterSingleLeadActivityCategory(filter: string, activityData: LeadActivityItemsType[]) {
		if (filter === LeadEventCategory.Notes) {
			return activityData.filter(ad => ad instanceof Note && ad.reminderDate.isEmpty);
		}
		if (filter === LeadEventCategory.Reminders) {
			return activityData.filter(ad => ad instanceof Note && ad.reminderDate.isDefined);
		}
		else {
			return activityData.filter(ad =>
				!(ad instanceof Note) &&
				LeadEventTypeObject.getTypesForCategory(filter).map((lto: LeadEventTypeObject) => lto.id).indexOf(ad.type) > -1);
		}
	}

	/**
	 * Filter single invoice activity category.
	 * @param {string} filter key of the category.
	 * @param {InvoiceActivityItemsType[]} activityData All data to filter.
	 * @return {InvoiceActivityItemsType[]} Filtered data by key.
	 */
	filterSingleInvoiceActivityCategory(filter: string, activityData: InvoiceActivityItemsType[]) {
		if (filter === InvoiceEventCategory.Notes) {
			return activityData.filter(ad => ad instanceof AccountingNote);
		}
		else {
			return activityData.filter(ad => !(ad instanceof AccountingNote) &&
				InvoiceEventTypeObject.getTypesForCategory(filter).map((ito: InvoiceEventTypeObject) => ito.id).indexOf(ad.type) >
				-1);
		}
	}

	/**
	 * Merge activity stream data and sort it by date - latest first.
	 * @param {JobEventsType[] | LeadEventType[] | InvoiceEventType[]} events job events.
	 * @param {Note[] | AccountingNote[]} notes job notes.
	 * @param payments invoice payments
	 */
	mergeActivityStreamItems(events: Array<JobEventsType | LeadEventType | InvoiceEventType>,
		notes: Array<Note | AccountingNote>, payments?: Array<Payment>) {
		let result = [].concat(events).concat(notes);
		if (payments) {
			result = result.concat(payments);
		}
		return result.sort(ActivityStreamUtils.sort);
	}
}

function mapStateToProps(state: RootState, props: ComponentProps) {
	const jobTypes = state.jobType.jobTypes.getOrElse([]);
	const jobType = props.job.flatMap(j => option(jobTypes.filter(jt => jt.id === j.data.jobTypeId)[0]));
	return {
		isLabourOnlyJob: jobType.map(i => i.labourOnly).getOrElse(false)
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		editJob: (job: Job, onSuccess: OnSuccess) => dispatch(
			editJob(job, onSuccess, (err) => notifyBugsnagAndShowToast(err, "Job details update"))
		)
	}
}

export default connect<ReduxProps, DispatchProps, ComponentProps>(mapStateToProps, mapDispatchToProps)(JobActivityStream);